/**
 * PublishConfirmationModal.jsx — Employer v1.1 Phase 0.6 Slice C.
 *
 * Interstitial confirm modal blocking the Publish dispatch. Copy
 * verbatim from pre-code plan §2.5. Includes:
 *   - the "draft → active is one-way" disclosure
 *   - a Cancel button (closes without dispatching)
 *   - a Publish CTA that is disabled-while-pending (W-3 guard)
 *
 * Surfaces backend error detail when the parent's publish promise
 * rejects (the parent passes the error message via the `error` prop).
 */
import { Loader2, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Operator-locked copy (pre-code plan §2.5). Tests pin the substring.
export const PUBLISH_CONFIRM_COPY =
  'Publishing makes the role visible to your account. You can soft-close (closing) or hard-close (closed) later, but draft → active is one-way.';

export default function PublishConfirmationModal({
  open,
  onCancel,
  onConfirm,
  pending,
  error,
}) {
  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(o) => { if (!o && !pending) onCancel(); }}
    >
      <DialogContent
        data-testid="publish-confirm-modal"
        aria-busy={pending ? 'true' : 'false'}
      >
        <DialogHeader>
          <DialogTitle data-testid="publish-confirm-title">
            Publish this role?
          </DialogTitle>
          <DialogDescription data-testid="publish-confirm-copy">
            {PUBLISH_CONFIRM_COPY}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
            data-testid="publish-confirm-error"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
            data-testid="publish-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={pending}
            data-testid="publish-confirm-go"
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing…
              </>
            ) : (
              'Publish role'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
